import {createEntityMeta,SyncStatus} from '../offline/entity.js';
import {
  JournalPhotoStatus,
  createJournalPhoto,
  updateJournalPhotoState
} from './journal-photo.js';

export const JOURNAL_PHOTO_ENTITY_TYPE='journal_photo';
export const JOURNAL_PHOTO_PAYLOAD_ENTITY_TYPE='journal_photo_payload';

function required(value,label){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(label+' is required');
  return text;
}

function clean(row){
  const copy={...row};
  delete copy.key;
  delete copy.entityType;
  return copy;
}

export function createJournalPhotoStore({
  repository,
  userId,
  now=()=>new Date().toISOString()
}={}){
  if(!repository?.get||!repository?.list||!repository?.put||!repository?.remove){
    throw new TypeError('repository with get/list/put/remove is required');
  }
  const owner=required(userId,'userId');

  function assertOwner(row){
    if(row?.userId!==owner)throw new Error('JOURNAL_PHOTO_OWNER_MISMATCH');
    return row;
  }

  async function create(input={}){
    const timestamp=now();
    const photo=createJournalPhoto({
      ...input,
      userId:owner,
      capturedAt:input.capturedAt||timestamp,
      photoStatus:JournalPhotoStatus.PENDING_UPLOAD
    });
    const existing=await repository.get(JOURNAL_PHOTO_ENTITY_TYPE,photo.id);
    if(existing)throw new Error('JOURNAL_PHOTO_ALREADY_EXISTS');
    const entity={
      ...createEntityMeta({id:photo.id,now:timestamp,syncStatus:SyncStatus.PENDING_CREATE}),
      ...photo
    };
    return repository.put(JOURNAL_PHOTO_ENTITY_TYPE,entity);
  }

  async function get(id){
    const row=await repository.get(JOURNAL_PHOTO_ENTITY_TYPE,required(id,'id'));
    if(!row)return null;
    const owned=assertOwner(row);
    return Object.freeze(
      owned.capturedAt
        ?owned
        :{...owned,capturedAt:owned.createdAt||now()}
    );
  }

  async function list({sessionId,itemId=undefined}={}){
    const rows=await repository.list(JOURNAL_PHOTO_ENTITY_TYPE);
    return Object.freeze((rows||[])
      .filter(row=>row.userId===owner)
      .filter(row=>!sessionId||row.sessionId===sessionId)
      .filter(row=>itemId===undefined||row.itemId===(itemId||null))
      .sort((a,b)=>String(a.createdAt||'').localeCompare(String(b.createdAt||''))||String(a.id).localeCompare(String(b.id))));
  }

  async function setState(id,changes={}){
    const current=await get(id);
    if(!current)throw new Error('JOURNAL_PHOTO_NOT_FOUND');
    const next=updateJournalPhotoState(clean(current),changes);
    return repository.put(JOURNAL_PHOTO_ENTITY_TYPE,{
      ...clean(current),
      ...next,
      updatedAt:now(),
      syncStatus:next.photoStatus===JournalPhotoStatus.UPLOADED
        ?SyncStatus.SYNCED
        :SyncStatus.PENDING_CREATE
    });
  }

  async function savePayload(id,file){
    const current=await get(id);
    if(!current)throw new Error('JOURNAL_PHOTO_NOT_FOUND');
    if(file==null)throw new TypeError('Photo file is required');

    let bytes=null;
    let fallbackFile=null;
    if(typeof file.arrayBuffer==='function'){
      bytes=await file.arrayBuffer();
      if(Number(bytes?.byteLength)!==Number(current.sizeBytes)){
        throw new TypeError('Photo payload size does not match metadata');
      }
    }else{
      fallbackFile=file;
    }

    const payload={
      ...createEntityMeta({id:current.id,now:now(),syncStatus:SyncStatus.SYNCED}),
      userId:owner,
      sessionId:current.sessionId,
      mimeType:current.mimeType,
      sizeBytes:current.sizeBytes,
      bytes,
      fallbackFile
    };
    return repository.put(JOURNAL_PHOTO_PAYLOAD_ENTITY_TYPE,payload);
  }

  async function getPayload(id){
    const row=await repository.get(JOURNAL_PHOTO_PAYLOAD_ENTITY_TYPE,required(id,'id'));
    if(!row)return null;
    if(row.userId!==owner)throw new Error('JOURNAL_PHOTO_PAYLOAD_OWNER_MISMATCH');
    return Object.freeze(row);
  }

  async function removePayload(id){
    await repository.remove(JOURNAL_PHOTO_PAYLOAD_ENTITY_TYPE,required(id,'id'));
    return true;
  }

  async function listPendingUploads(){
    const rows=await list({});
    const pending=new Set([
      JournalPhotoStatus.PENDING_UPLOAD,
      JournalPhotoStatus.UPLOAD_FAILED,
      JournalPhotoStatus.PENDING_METADATA,
      JournalPhotoStatus.METADATA_FAILED
    ]);
    return Object.freeze(rows.filter(row=>pending.has(row.photoStatus)));
  }

  async function removeLocal(id){
    const current=await get(id);
    if(!current)return false;
    await repository.remove(JOURNAL_PHOTO_ENTITY_TYPE,current.id);
    await repository.remove(JOURNAL_PHOTO_PAYLOAD_ENTITY_TYPE,current.id);
    return true;
  }

  return Object.freeze({
    create,get,list,setState,savePayload,getPayload,removePayload,listPendingUploads,removeLocal,
    markUploadFailed:(id,errorCode)=>setState(id,{photoStatus:JournalPhotoStatus.UPLOAD_FAILED,errorCode}),
    markPendingMetadata:(id,storagePath)=>setState(id,{photoStatus:JournalPhotoStatus.PENDING_METADATA,storagePath,errorCode:null}),
    markMetadataFailed:(id,errorCode)=>setState(id,{photoStatus:JournalPhotoStatus.METADATA_FAILED,errorCode}),
    markUploaded:(id,storagePath)=>setState(id,{photoStatus:JournalPhotoStatus.UPLOADED,storagePath,errorCode:null}),
    markDeleteFailed:(id,errorCode)=>setState(id,{photoStatus:JournalPhotoStatus.DELETE_FAILED,errorCode})
  });
}
